import { datadogLogs } from '@datadog/browser-logs';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Controller } from '@hotwired/stimulus';

declare global {
  interface Window {
    turnstile?: Turnstile.Turnstile;
  }
}

async function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = false;
    script.defer = false;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function logEvent(state: string): void {
  void FingerprintJS.load({ monitoring: false })
    .then(fingerprint => fingerprint.get())
    .then(visitorId => datadogLogs.logger.info('CaptchaEvent', { state, visitorId }));
}

export default class extends Controller<HTMLElement> {
  static targets = ['label', 'widget', 'submit'];

  static values = { siteKey: String };

  declare siteKeyValue: string;

  declare labelTarget: HTMLElement;

  declare widgetTarget: HTMLElement;

  declare submitTarget: HTMLButtonElement;

  #widgetId: string | null | undefined = undefined;

  async connect(): Promise<void> {
    try {
      await loadTurnstileScript();
    } catch {
      logEvent('script_load_error');
      return;
    }

    if (!window.turnstile) {
      logEvent('turnstile_not_loaded');
      return;
    }

    window.turnstile.ready(() => {
      logEvent('ready');
      this.#renderWidget();
    });
  }

  disconnect(): void {
    this.#removeWidget();
  }

  onAjaxSuccess(): void {
    this.#removeWidget();
  }

  onAjaxError(): void {
    logEvent('submit_error');
    this.#renderWidget();
  }

  #renderWidget(): void {
    if (!window.turnstile) return;

    logEvent('loading');

    this.labelTarget.hidden = false;
    this.#widgetId = window.turnstile.render(this.widgetTarget, {
      action: 'login',
      size: 'flexible',
      theme: 'light',
      sitekey: this.siteKeyValue,
      callback: this.#onSuccess,
      language: 'en-US',
      'error-callback': this.#onError,
      'expired-callback': this.#onExpired,
      'timeout-callback': this.#onTimeout,
      'unsupported-callback': this.#onUnsupported,
      'before-interactive-callback': this.#onBeforeInteractive,
      'after-interactive-callback': this.#onAfterInteractive,
    });

    logEvent('loaded');
  }

  #removeWidget(): void {
    if (!this.#widgetId) return;

    logEvent('removing_widget');
    window.turnstile?.remove(this.#widgetId);
    this.#widgetId = undefined;
    this.labelTarget.hidden = true;
    logEvent('widget_removed');
  }

  #resetWidget(): void {
    if (!this.#widgetId) return;

    logEvent('resetting_widget');
    window.turnstile?.reset(this.#widgetId);
    logEvent('widget_reset');
  }

  #onSuccess = (): void => {
    this.submitTarget.disabled = false;
    logEvent('success');
  };

  #onError = (): void => {
    this.submitTarget.disabled = false;
    logEvent('error');
  };

  #onExpired = (): void => {
    logEvent('expired');
    this.submitTarget.disabled = true;
    this.#resetWidget();
  };

  #onTimeout = (): void => {
    logEvent('timeout');
    this.submitTarget.disabled = true;
    this.#resetWidget();
  };

  #onUnsupported = (): void => {
    logEvent('unsupported_browser');
    this.submitTarget.disabled = false;
  };

  #onBeforeInteractive = (): void => {
    logEvent('before_captcha_interaction');
  };

  #onAfterInteractive = (): void => {
    logEvent('after_captcha_interaction');
  };
}
